import { Button, Flex, Result, Typography } from 'antd';
import type { AppError } from '../../api/errors';
import { useRetryCountdown } from '../../lib/retry-countdown';

/**
 * Boot could not reach the API (docs/07-implementation-roadmap.md F7.5).
 *
 * **This screen exists because of one specific failure.** The first request of
 * every page load is `POST /auth/refresh`, and `API_RATE_LIMIT_MAX` is a blanket
 * 100 per minute per IP that the whole office shares. So the throttle the office
 * meets first is the one that lands here, before any screen has rendered — and
 * until F7.5 the app answered it by clearing the session and showing the login
 * form. Which is the worst available answer: it discards a session that was
 * never over, it asks for a password, and the login endpoint is refused by the
 * same limiter, so the person's conclusion is "the system is broken" and the
 * next thing they do is reload, spending more of the budget they are short of.
 *
 * What it says instead is what is true: the system is busy, here is how long,
 * and nothing has been lost. `restoreSession` leaves the session store untouched
 * in this state, so the retry below can still find a live session behind the
 * cookie.
 *
 * The escape hatch is not decoration. If the API is down for the afternoon, or
 * somebody wants to sign in as another user, this screen must not be a room with
 * no door.
 */
export function BootUnavailable({
  error,
  onRetry,
  onSignIn,
}: {
  error: AppError;
  onRetry: () => void;
  onSignIn: () => void;
}) {
  const countdown = useRetryCountdown(error.retryAt);
  const waiting = countdown > 0;
  const throttled = error.kind === 'rateLimited';

  return (
    <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 24 }}>
      <Result
        status={throttled ? 'warning' : '500'}
        title={throttled ? 'Sistem sedang sibuk' : 'Tidak dapat menghubungi sistem'}
        subTitle={error.userMessage}
        extra={[
          <Button key="retry" type="primary" disabled={waiting} onClick={onRetry}>
            {waiting ? `Coba lagi (${countdown})` : 'Coba lagi'}
          </Button>,
          <Button key="sign-in" onClick={onSignIn}>
            Ke halaman masuk
          </Button>,
        ]}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0, textAlign: 'center' }}>
          Anda belum keluar dari sistem — sesi Anda masih tersimpan dan akan dilanjutkan saat berhasil tersambung.
        </Typography.Paragraph>
      </Result>
    </Flex>
  );
}
